// src/components/dashboard/VisitorsStatistics.jsx
import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Avatar,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { People } from '@mui/icons-material';
import axios from 'axios';
import { API_GET_EXPORT_VISITORS, API_GET_VISITORS_COUNT } from '../../Util/URLConstants';
import { downloadBlob } from '../../Util/ApiHelper';
import SubscriptionDialog from '../../Util/SubscriptionDialog';
import MUITypography from '../../Components/MUITypography';
import StorageFactory from '../../Util/StorageFactory';
import { ACCESS_RESTRICTED_MESSAGE, SESSION_USER_ROLES_PERMISSIONS } from '../../Util/VMConstants';
import { checkIsPermissionExist } from '../../Util/UtilHelper';
import { align_column, vmPageTitleSx } from '../../Styles/VMStyles';

const safeExtractCount = (res) => {
  if (!res) return 0;
  if (typeof res === 'number') return res;
  if (res.data && typeof res.data.count === 'number') return res.data.count;
  if (res.data && res.data.data && typeof res.data.data.count === 'number') return res.data.data.count;
  if (typeof res.count === 'number') return res.count;
  if (res.data && typeof res.data === 'number') return res.data;
  return 0;
};

const VisitorsStatistics = ({ defaultFrom, defaultTo, userID, canAccessPremiumFeatures }) => {
  const [fromDate, setFromDate] = useState(defaultFrom || '');
  const [toDate, setToDate] = useState(defaultTo || '');
  const [count, setCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const abortRef = useRef(null);
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);



  const sessionFactory = useMemo(
    () => new StorageFactory("session"),
    []
  );

  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  const isDownloadRecordPermission = checkIsPermissionExist(
    VM_PERMISSIONS,
    "VM051"
  );

  useEffect(() => {
    if (defaultFrom) setFromDate(defaultFrom);
    if (defaultTo) setToDate(defaultTo);
  }, [defaultFrom, defaultTo]);

  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const fetchCount = async () => {
      try {
        const res = await axios.get(API_GET_VISITORS_COUNT, {
          params: {
            from: fromDate || undefined,
            to: toDate || undefined,
            user_id: userID || undefined,
          },
          signal: controller.signal,
        });

        const cnt = safeExtractCount(res?.data ?? res);
        setCount(Number(cnt || 0));
      } catch (err) {
        if (axios.isCancel && axios.isCancel(err)) {
          return;
        }
        if (err.name === 'CanceledError' || err.message === 'canceled') {
          return;
        }
        console.error('VisitorsStatistics error:', err);
        setCount(0);
      }
    };

    fetchCount();

    return () => {
      controller.abort();
    };
  }, [fromDate, toDate, userID]);

  const handleExport = async () => {
  if(isDownloadRecordPermission === true){
        if (!canAccessPremiumFeatures) {
          setOpenSubscribeDialog(true);
          return;
        }
        setExportLoading(true);
        try {
          const res = await axios.get(API_GET_EXPORT_VISITORS, {
            params: { from: fromDate || undefined, to: toDate || undefined, user_id: userID || undefined },
            responseType: 'blob',
          });
          downloadBlob(res.data, `visitors_${Date.now()}.xlsx`);
        } catch (err) {
          console.error('Export visitors error:', err);
        } finally {
          setExportLoading(false);
        }
  }else{
    alert(ACCESS_RESTRICTED_MESSAGE);
  }
  };

  return (
    <Card sx={{ boxShadow: 1 ,border:'1px solid #E0E0E0'}}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ ...align_column, alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <MUITypography variant="pageTitle" sx={{ ...vmPageTitleSx, mb: 1 }}> Visitor's Statistics</MUITypography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mb: 3 }}>
            <People sx={{ width: 40, height: 40 }} />
          </Avatar>
          <MUITypography  sx={{fontSize:'34px', fontWeight: 700, mb: 1 }}>{count.toLocaleString()}</MUITypography>
          <MUITypography color="text.secondary"sx={{fontSize:'16px', fontWeight: 500, mb: 1 }} >Total Visitors</MUITypography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={exportLoading || !canAccessPremiumFeatures}
            >
              {exportLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : canAccessPremiumFeatures ? (
                'Download'
              ) : (
                'Upgrade to Download'
              )}
            </Button>
          </Box>
        </Box>
      </CardContent>

      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = '/subscription';
        }}
      />
    </Card>
  );
};

export default VisitorsStatistics;
